import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronDown,
  Dumbbell,
  FileText,
  MoreHorizontal,
  MoreVertical,
  Plus,
  SkipForward,
  SlidersHorizontal,
  Sun,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button, BotonEnlace } from '@/components/Button';
import { EstadoVacio, Pastilla } from '@/components/Card';
import { Segmentado } from '@/components/Segmentado';
import { NumberField, TextField } from '@/components/Field';
import { actualizarSettings, db, leerSettings } from '@/db/schema';
import {
  actualizarSerie,
  agregarSerie,
  cambiarDescanso,
  cambiarUnidadEnEntrenamiento,
  descartarEntrenamiento,
  fijarCargaDeLasQueFaltan,
  guardarNotasEntrenamiento,
  guardarPausaYVariante,
  leerEnCurso,
  leerEntrenamiento,
  marcarSerie,
  moverEjercicioEnEntrenamiento,
  quitarEjercicioDeEntrenamiento,
  quitarSerie,
  restaurarEjercicioEnEntrenamiento,
  restaurarSerie,
  separarBiserie,
  seriesSinMarcar,
  terminarEntrenamiento,
  type CambiosSerie,
  type EjercicioEnCurso,
} from '@/db/entrenamientos';
import { mensajeDeError } from '@/db/tx';
import type { TipoEjercicio, TipoSerie, Workout, WorkoutExercise, WorkoutSet } from '@/db/types';
import {
  agruparPorBiserie,
  ayudaDeCarga,
  formatearReloj,
  lugarAlRestaurar,
  segundosRestantes,
  textoDescanso,
  textoDuracion,
  textoObjetivo,
  textoSerie,
  vecinosDe,
  type VecinosEnSesion,
} from '@/domain/entrenamiento';
import { fechaCorta } from '@/domain/hoy';
import { nombreDia } from '@/domain/dates';
import { horaLocalDe } from '@/domain/time';
import { formatearNumero, leerDecimal } from '@/domain/numbers';
import { sugerenciaDeProgresion } from '@/domain/progresion';
import { ETIQUETAS_MODALIDAD, modalidadDe, textoPausaYVariante } from '@/domain/modalidad';
import { ETIQUETAS_UNIDAD_CARGA, UNIDADES_CARGA, capturarCarga, type UnidadCarga, type UnidadPeso } from '@/domain/units';
import {
  cancelarPitido,
  escucharEstadoDelAviso,
  estadoDelAviso,
  fijarAvisoConPantallaBloqueada,
  prepararSonido,
  programarFinDeDescanso,
  type EstadoAviso,
} from '@/lib/sonido';
import { avisarFinDeDescanso, olvidarAvisoDeDescanso } from '@/lib/avisos';
import { sincronizarAvisoDelServidor, suscripcionActual } from '@/lib/avisos-servidor';

/**
 * ¿El aviso del descanso va a llegar por el servidor? Solo si está encendido
 * Y este teléfono está suscrito de verdad: si la suscripción no existe (otro
 * navegador, permiso revocado), el audio sigue siendo la única forma de que
 * suene con la pantalla bloqueada y no hay que desactivarlo.
 */
function useAvisoPorServidor(encendido: boolean): boolean {
  const [suscrito, setSuscrito] = useState(false);
  useEffect(() => {
    if (!encendido) {
      setSuscrito(false);
      return;
    }
    let vigente = true;
    void suscripcionActual().then((s) => {
      if (vigente) setSuscrito(s !== null);
    });
    return () => {
      vigente = false;
    };
  }, [encendido]);
  return suscrito;
}
import { PANTALLA_ENCENDIDA_SOPORTADA, controlPantalla, usePantallaEncendida, type EstadoPantalla } from '@/lib/pantalla';

const TIPOS_SERIE: readonly { valor: TipoSerie; etiqueta: string }[] = [
  { valor: 'normal', etiqueta: 'Normal' },
  { valor: 'calentamiento', etiqueta: 'Calentamiento' },
  { valor: 'dropset', etiqueta: 'Drop set' },
  { valor: 'fallo', etiqueta: 'Al fallo' },
];

const conMayuscula = (t: string) => t.charAt(0).toLocaleUpperCase('es-MX') + t.slice(1);

/**
 * Acceso corto del encabezado del entrenamiento (0.11.0): una línea con su
 * ícono que abre algo o lo activa, sin ocupar un botón de ancho completo.
 */
function Chip({
  icono: Icono,
  texto,
  detalle,
  abierto,
  marcado,
  disabled = false,
  etiqueta,
  onClick,
}: {
  icono: LucideIcon;
  texto: string;
  /** Lo que está pasando ahora, como "Activa". */
  detalle?: string;
  /** Para los que despliegan algo: gira la flecha y lo anuncia. */
  abierto?: boolean;
  /** Para los que encienden algo: se ve distinto cuando está encendido. */
  marcado?: boolean;
  disabled?: boolean;
  etiqueta: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      {...(abierto === undefined ? {} : { 'aria-expanded': abierto })}
      {...(marcado === undefined ? {} : { 'aria-pressed': marcado })}
      aria-label={etiqueta}
      disabled={disabled}
      onClick={onClick}
      className={`flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-[15px] px-3 py-2 text-left text-[12px] ${
        marcado === true ? 'bg-marca-suave text-texto' : 'bg-superficie text-texto'
      } disabled:text-texto-suave`}
    >
      <Icono aria-hidden="true" className={`size-4 shrink-0 ${disabled ? 'text-texto-suave' : 'text-marca'}`} />
      {/* Un solo texto, no dos: así se recorta de verdad a 320 px y no hay un
          segundo tono que no llegue a 4.5:1 sobre el acento suave */}
      <span className="min-w-0 flex-1 truncate">
        {detalle === undefined ? texto : `${texto} · ${detalle}`}
      </span>
      {abierto !== undefined && (
        <ChevronDown aria-hidden="true" className={`size-4 shrink-0 text-texto-suave ${abierto ? 'rotate-180' : ''}`} />
      )}
    </button>
  );
}

/** "Activa", "Falta un toque", "Soltada": lo corto que cabe en el chip. */
function textoCortoDePantalla(estado: EstadoPantalla): string {
  switch (estado.tipo) {
    case 'activa':
      return 'Activa';
    case 'pidiendo':
      return 'Activando…';
    case 'falta_toque':
      return 'Falta un toque';
    case 'liberada':
      return 'La soltó el sistema';
    case 'rechazada':
      return 'No se pudo';
    default:
      return 'Encendida';
  }
}

/**
 * El entrenamiento en curso (Req. 11, 12), para usar con una mano: cada
 * cambio se guarda al momento, la palomita de 56 puntos marca la serie y
 * empieza el descanso, y la barra de descanso queda fija abajo.
 */
export function EntrenamientoPage() {
  const enCurso = useLiveQuery(() => leerEnCurso(), []);
  if (enCurso === undefined) return null;
  if (enCurso === null) {
    return (
      <div>
        <PageHeader titulo="Sin entrenamiento en curso." volver={{ a: '/gym', texto: 'Gym' }} />
        <EstadoVacio icono={Dumbbell} titulo="No hay nada abierto." descripcion="Empieza uno desde una rutina o como entrenamiento libre.">
          <BotonEnlace to="/gym" variante="secundario">
            Ir a Gym
          </BotonEnlace>
        </EstadoVacio>
      </div>
    );
  }
  return <Entrenamiento key={enCurso.id} id={enCurso.id} />;
}

function useAhora(cadaMs: number): Date {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setAhora(new Date()), cadaMs);
    return () => window.clearInterval(t);
  }, [cadaMs]);
  return ahora;
}

/** Ejercicios seguidos de la misma biserie van juntos. */
function agrupar(ejercicios: readonly EjercicioEnCurso[]): EjercicioEnCurso[][] {
  return agruparPorBiserie(ejercicios, (e) => e.we.biserie);
}

/**
 * Cambiar el orden de los ejercicios a mitad del entrenamiento (Req. 11.6),
 * por ejemplo si un aparato está ocupado. Una biserie se mueve completa.
 */
function OrdenDeEjercicios({
  ejercicios,
  onAccion,
  onListo,
}: {
  ejercicios: readonly EjercicioEnCurso[];
  onAccion: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
  onListo: () => void;
}) {
  const bloques = agrupar(ejercicios);
  const [anuncio, setAnuncio] = useState('');
  const botones = useRef(new Map<string, { subir: HTMLButtonElement | null; bajar: HTMLButtonElement | null }>());

  async function mover(id: string, nombre: string, i: number, direccion: -1 | 1) {
    const ok = await onAccion(async () => {
      await moverEjercicioEnEntrenamiento(id, direccion);
      return true;
    });
    if (ok !== true) return;
    const nueva = i + direccion;
    setAnuncio(`${nombre}: ahora ${nueva + 1} de ${bloques.length}.`);
    // En un extremo, el botón que se tocó se desactiva: el foco pasa al otro de la fila
    window.requestAnimationFrame(() => {
      const fila = botones.current.get(id);
      if (nueva === 0) fila?.bajar?.focus();
      else if (nueva === bloques.length - 1) fila?.subir?.focus();
    });
  }

  return (
    <section aria-labelledby="titulo-orden" className="rounded-panel bg-superficie p-4">
      <h2 id="titulo-orden" className="text-[17px] tracking-[-0.25px] text-texto">
        Orden de los ejercicios
      </h2>
      <p className="mt-1 text-[12px] leading-relaxed text-texto-suave">
        Sube o baja cada uno; una biserie se mueve completa. Lo que ya registraste no cambia.
      </p>
      <p className="sr-only" aria-live="polite">
        {anuncio}
      </p>
      <ol className="mt-2 divide-y divide-borde">
        {bloques.map((b, i) => {
          const primero = b[0];
          if (primero === undefined) return null;
          const nombres = b.map((e) => e.ejercicio?.nombre ?? 'Ejercicio').join(' + ');
          const nombre = b.length > 1 ? `Biserie: ${nombres}` : nombres;
          return (
            <li key={primero.we.id} className="flex items-center gap-2 py-2">
              <span className="w-6 shrink-0 text-[13px] tabular-nums text-texto-suave">{i + 1}</span>
              <span className="min-w-0 flex-1 text-[14px] leading-snug text-texto">{nombre}</span>
              <button
                type="button"
                ref={(b) => {
                  botones.current.set(primero.we.id, { subir: b, bajar: botones.current.get(primero.we.id)?.bajar ?? null });
                }}
                aria-label={`Subir ${nombre}`}
                disabled={i === 0}
                onClick={() => void mover(primero.we.id, nombre, i, -1)}
                className="grid size-11 shrink-0 place-items-center rounded-full bg-marca-suave text-texto disabled:opacity-40"
              >
                <ArrowUp className="size-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                ref={(b) => {
                  botones.current.set(primero.we.id, { subir: botones.current.get(primero.we.id)?.subir ?? null, bajar: b });
                }}
                aria-label={`Bajar ${nombre}`}
                disabled={i === bloques.length - 1}
                onClick={() => void mover(primero.we.id, nombre, i, 1)}
                className="grid size-11 shrink-0 place-items-center rounded-full bg-marca-suave text-texto disabled:opacity-40"
              >
                <ArrowDown className="size-5" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ol>
      <Button variante="secundario" icono={Check} className="mt-3" onClick={onListo}>
        Listo
      </Button>
    </section>
  );
}

function Entrenamiento({ id }: { id: string }) {
  const navegar = useNavigate();
  const detalle = useLiveQuery(() => leerEntrenamiento(id), [id]);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const notasRutina = useLiveQuery(async () => {
    const w = await db.workouts.get(id);
    return w?.routineId == null ? null : ((await db.routines.get(w.routineId))?.notas ?? null);
  }, [id]);
  const ahora = useAhora(30_000);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [terminando, setTerminando] = useState<number | null>(null);
  const [confirmarDescartar, setConfirmarDescartar] = useState(false);
  const [ordenando, setOrdenando] = useState(false);
  const [quitado, setQuitado] = useState<EjercicioQuitado | null>(null);
  const [notasAbiertas, setNotasAbiertas] = useState(false);
  const [masAbierto, setMasAbierto] = useState(false);
  const pantalla = usePantallaEncendida(ajustes?.pantallaEncendida ?? false);

  // El aviso sigue a la hora de fin guardada; sonido.ts lo reprograma al volver de segundo plano
  const descanso = detalle?.workout.descanso ?? null;
  const terminaEn = descanso?.terminaEn ?? null;
  // La opción de avisar con la pantalla bloqueada (0.12.0) se aplica antes de
  // armar el aviso, para que elija la forma correcta desde el primer descanso
  const avisoBloqueada = ajustes?.avisoConPantallaBloqueada ?? false;
  // Con el aviso del servidor funcionando NO se toma el audio (0.16.0): ese
  // método reproduce una pista de 1:30, así que pausa la música y ocupa el
  // reproductor de la pantalla bloqueada durante todo el descanso. Si el
  // aviso llega por Web Push eso no hace falta, y los tonos del final siguen
  // sonando mezclados con la música, como cualquier temporizador web
  const porServidor = useAvisoPorServidor(ajustes?.avisoConAppCerrada ?? false);
  useEffect(() => {
    fijarAvisoConPantallaBloqueada(avisoBloqueada && !porServidor);
  }, [avisoBloqueada, porServidor]);
  useEffect(() => {
    olvidarAvisoDeDescanso();
    programarFinDeDescanso(terminaEn);
    // Y el aviso con la app cerrada (0.15.0), que lo manda el servidor en el
    // instante exacto. Con terminaEn en null cancela, que es lo que pasa al
    // saltar el descanso o al terminar el entrenamiento. No lanza nunca: sin
    // internet el sonido sigue siendo lo principal
    void sincronizarAvisoDelServidor(terminaEn);
  }, [terminaEn]);
  useEffect(() => () => cancelarPitido(), []);

  if (detalle === undefined || ajustes === undefined || notasRutina === undefined) return null;
  if (detalle === null) return null;
  const { workout, ejercicios } = detalle;
  const minutos = textoDuracion((ahora.getTime() - Date.parse(workout.inicio)) / 1000);
  const casa = modalidadDe(workout) === 'casa';
  const grupos = agrupar(ejercicios);
  // El aviso del ejercicio quitado va donde "Deshacer" lo va a devolver, con la
  // misma regla que la base (0.3.9: después de cambiar el orden quedaba en otro lado)
  const posicionDelQuitado = quitado === null ? -1 : grupoDelLugar(grupos, lugarAlRestaurar(ejercicios.map((x) => x.we), quitado.we, quitado.vecinos).posicion);
  const avisoDelQuitado =
    quitado === null ? null : (
      <AvisoDeshacer
        texto={`Quitaste ${quitado.nombre}${
          quitado.series.length === 0 ? '' : quitado.series.length === 1 ? ' y su serie' : ` y sus ${quitado.series.length} series`
        }. Deshacerlo lo devuelve igual, con lo que anotaste.`}
        onDeshacer={() =>
          void accion(async () => {
            await restaurarEjercicioEnEntrenamiento(quitado.we, quitado.series, quitado.vecinos);
            setQuitado(null);
          })
        }
        onCerrar={() => setQuitado(null)}
      />
    );

  async function accion<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(mensajeDeError(e));
      return undefined;
    }
  }

  /** Quita el ejercicio y guarda cómo estaba y entre quiénes, para "Deshacer". */
  async function quitarEjercicio(q: Omit<EjercicioQuitado, 'vecinos'>) {
    const vecinos = vecinosDe(
      ejercicios.map((x) => x.we),
      q.we.id,
    );
    await accion(async () => {
      await quitarEjercicioDeEntrenamiento(q.we.id);
      setQuitado({ ...q, vecinos });
    });
  }

  async function marcar(serie: WorkoutSet, hecha: boolean, pendientes: CambiosSerie | null) {
    prepararSonido();
    setAviso(null);
    await accion(async () => {
      if (pendientes !== null) await actualizarSerie(serie.id, pendientes);
      const despues = await marcarSerie(serie.id, hecha);
      if (despues.tipo === 'sigue') {
        const otro = ejercicios.find((e) => e.we.id === despues.workoutExerciseId);
        setAviso(`Biserie: sigue con ${otro?.ejercicio?.nombre ?? 'el otro ejercicio'}.`);
      }
    });
  }

  async function pedirTerminar() {
    const sin = await accion(() => seriesSinMarcar(workout.id));
    if (sin === undefined) return;
    if (sin > 0) {
      setTerminando(sin);
      return;
    }
    await terminar();
  }

  async function terminar() {
    const ok = await accion(async () => {
      await terminarEntrenamiento(workout.id);
      return true;
    });
    if (ok === true) void navegar(`/gym/entrenamiento/${workout.id}/resumen`, { replace: true });
  }

  const todasLasSeries = ejercicios.flatMap((x) => x.series);
  const seriesHechas = todasLasSeries.filter((s) => s.hecha).length;

  return (
    <div className={`space-y-4 ${descanso !== null ? 'pb-36' : ''}`}>
      <PageHeader
        compacto
        titulo={workout.nombre}
        descripcion={`${conMayuscula(nombreDia(workout.fecha))} · ${ETIQUETAS_MODALIDAD[modalidadDe(workout)]} · ${minutos} · desde las ${horaLocalDe(workout.inicio)}`}
        volver={{ a: '/gym', texto: 'Gym', etiqueta: 'Volver a Gym; el entrenamiento sigue abierto' }}
        extra={
          <button
            type="button"
            aria-expanded={masAbierto}
            aria-label="Notas y descartar este entrenamiento"
            onClick={() => setMasAbierto((v) => !v)}
            className="grid size-11 shrink-0 place-items-center rounded-full text-texto-suave active:bg-marca-suave"
          >
            <MoreHorizontal aria-hidden="true" className="size-5" />
          </button>
        }
      />

      {/* Cuántas series van, con su barra: el avance de la sesión de un vistazo */}
      {todasLasSeries.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between gap-2 text-[12px] text-texto-suave">
            <span className="tabular-nums">
              {seriesHechas} de {todasLasSeries.length} series
            </span>
            <span className="tabular-nums">{Math.round((seriesHechas / todasLasSeries.length) * 100)} %</span>
          </div>
          <div aria-hidden="true" className="mt-1 h-1.5 overflow-hidden rounded-full bg-superficie-suave">
            <div
              className="h-full rounded-full bg-marca"
              style={{ width: `${Math.round((seriesHechas / todasLasSeries.length) * 100)}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {notasRutina !== null && (
          <Chip
            icono={FileText}
            texto="Calentamiento e indicaciones"
            abierto={notasAbiertas}
            etiqueta="Calentamiento e indicaciones de la rutina"
            onClick={() => setNotasAbiertas((v) => !v)}
          />
        )}
        {PANTALLA_ENCENDIDA_SOPORTADA ? (
          <Chip
            icono={Sun}
            texto="Pantalla encendida"
            detalle={ajustes.pantallaEncendida ? textoCortoDePantalla(pantalla) : 'Apagada'}
            marcado={ajustes.pantallaEncendida}
            etiqueta={`Mantener la pantalla encendida: ${ajustes.pantallaEncendida ? 'activada' : 'desactivada'}`}
            onClick={() => {
              const m = !ajustes.pantallaEncendida;
              // En el mismo toque: Safari solo la concede así (0.3.8)
              if (m) controlPantalla().pedirConToque();
              void accion(() => actualizarSettings({ pantallaEncendida: m }));
            }}
          />
        ) : (
          <Chip icono={Sun} texto="Pantalla encendida" detalle="No disponible" disabled etiqueta="Mantener la pantalla encendida: no disponible en este navegador" />
        )}
      </div>

      {notasAbiertas && notasRutina !== null && (
        <p className="rounded-panel bg-superficie p-4 text-[13px] leading-relaxed text-texto">{notasRutina}</p>
      )}
      {PANTALLA_ENCENDIDA_SOPORTADA
        ? ajustes.pantallaEncendida && <EstadoDePantalla estado={pantalla} />
        : <p className="text-[12px] leading-relaxed text-texto-suave">
            Este navegador no permite mantener la pantalla encendida. En el iPhone, la app instalada lo permite desde iOS 18.4.
          </p>}

      {masAbierto && (
        <div className="space-y-3 rounded-panel bg-superficie p-4">
          <NotasEntrenamiento workout={workout} onAccion={accion} />
          {confirmarDescartar ? (
            <div className="space-y-2 rounded-[15px] bg-alerta-suave p-3">
              <p className="text-[13px] text-texto">¿Descartar todo este entrenamiento? No se guarda nada de lo que registraste hoy.</p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variante="peligro"
                  onClick={() =>
                    void accion(async () => {
                      await descartarEntrenamiento(workout.id);
                      void navegar('/gym', { replace: true });
                    })
                  }
                >
                  Sí, descartar
                </Button>
                <Button variante="secundario" onClick={() => setConfirmarDescartar(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button variante="fantasma" icono={Trash2} onClick={() => setConfirmarDescartar(true)}>
              Descartar entrenamiento
            </Button>
          )}
        </div>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo">
          {error}
        </Banner>
      )}

      {ejercicios.length === 0 && (
        <EstadoVacio titulo="Sin ejercicios todavía." descripcion="Agrega el primero para empezar a registrar." />
      )}

      {grupos.map((grupo, i) => (
        <Fragment key={grupo[0]?.we.id ?? i}>
        {quitado !== null && posicionDelQuitado === i && avisoDelQuitado}
        {grupo.length > 1 ? (
          <section aria-label="Biserie" className="space-y-3 rounded-panel border border-marca p-3">
            <div className="flex items-start justify-between gap-2 px-1">
              <p className="text-[12px] leading-relaxed text-texto-suave">
                <span className="font-medium text-marca">Biserie:</span> alterna los dos. Descanso al terminar la serie de los dos:{' '}
                {textoDescanso(Math.max(...grupo.map((e) => e.we.descansoBiserieSeg ?? e.we.descansoSeg)))}.
              </p>
            </div>
            {grupo.map((e) => (
              <TarjetaEjercicio
                key={e.we.id}
                e={e}
                enBiserie
                abiertoAlInicio={e.series.some((s) => !s.hecha)}
                casa={casa}
                preferida={ajustes.unidadPeso}
                incrementoPredeterminadoKg={ajustes.incrementoPredeterminadoKg}
                onMarcar={marcar}
                onAccion={accion}
                onQuitar={quitarEjercicio}
              />
            ))}
            <Button variante="fantasma" onClick={() => void accion(() => separarBiserie(workout.id, grupo[0]?.we.biserie ?? ''))}>
              Hacer por separado
            </Button>
          </section>
        ) : (
          grupo[0] !== undefined && (
            <TarjetaEjercicio
              e={grupo[0]}
              enBiserie={false}
              abiertoAlInicio={grupo[0].series.some((s) => !s.hecha)}
              casa={casa}
              preferida={ajustes.unidadPeso}
              incrementoPredeterminadoKg={ajustes.incrementoPredeterminadoKg}
              onMarcar={marcar}
              onAccion={accion}
              onQuitar={quitarEjercicio}
            />
          )
        )}
        </Fragment>
      ))}

      {/* "Deshacer" del ejercicio quitado, donde estaba (al final si ya no hay nada después) */}
      {quitado !== null && posicionDelQuitado === grupos.length && avisoDelQuitado}

      {ordenando && <OrdenDeEjercicios ejercicios={ejercicios} onAccion={accion} onListo={() => setOrdenando(false)} />}

      <div className={`grid gap-2 ${grupos.length > 1 && !ordenando ? 'grid-cols-2' : ''}`}>
        <BotonEnlace to={`/gym/ejercicios?para=entrenamiento:${workout.id}`} variante="secundario" icono={Plus}>
          Ejercicio
        </BotonEnlace>
        {grupos.length > 1 && !ordenando && (
          <Button variante="secundario" icono={ArrowUpDown} onClick={() => setOrdenando(true)}>
            Ordenar
          </Button>
        )}
      </div>

      {terminando !== null ? (
        <div className="space-y-2 rounded-panel bg-aviso-suave p-4">
          <p className="text-[13px] text-texto">
            {terminando === 1 ? 'Hay 1 serie sin marcar.' : `Hay ${terminando} series sin marcar.`} Al terminar se descartan.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => void terminar()}>Descartarlas y terminar</Button>
            <Button variante="secundario" onClick={() => setTerminando(null)}>
              Volver
            </Button>
          </div>
        </div>
      ) : (
        <Button icono={Check} tamano="grande" onClick={() => void pedirTerminar()}>
          Terminar entrenamiento
        </Button>
      )}

      <BarraDescanso workout={workout} aviso={aviso} onAccion={accion} />
    </div>
  );
}

/**
 * En casa: la variante y la pausa en cada repetición de este ejercicio hoy.
 * Empiezan con las de la vez pasada en casa (o las de la rutina) y se guardan
 * al salir de cada campo.
 */
function PausaYVarianteDeEjercicio({
  e,
  onAccion,
}: {
  e: EjercicioEnCurso;
  onAccion: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
}) {
  const nombre = e.ejercicio?.nombre ?? 'este ejercicio';
  const [variante, setVariante] = useState(e.we.variante ?? '');
  const [pausa, setPausa] = useState(e.we.pausaSeg === null || e.we.pausaSeg === undefined ? '' : String(e.we.pausaSeg));
  const guardarVariante = () => {
    if (variante.trim() === (e.we.variante ?? '')) return;
    void onAccion(() => guardarPausaYVariante(e.we.id, { variante }));
  };
  const guardarPausa = () => {
    const texto = pausa.trim();
    const valor = texto === '' ? null : leerDecimal(texto);
    if (texto !== '' && valor === null) return void onAccion(async () => Promise.reject(new Error('Revisa la pausa: son segundos.')));
    if (valor === (e.we.pausaSeg ?? null)) return;
    void onAccion(() => guardarPausaYVariante(e.we.id, { pausaSeg: valor }));
  };
  return (
    <div className="mt-3 grid grid-cols-2 gap-2" role="group" aria-label={`Variante y pausa de ${nombre}`}>
      <div onBlur={guardarVariante} className="min-w-0">
        <TextField etiqueta="Variante" valor={variante} onCambio={setVariante} maxLength={60} lista={e.variantes} />
      </div>
      <div onBlur={guardarPausa} className="min-w-0">
        <NumberField etiqueta="Pausa por rep. (s)" valor={pausa} onCambio={setPausa} decimal={false} />
      </div>
    </div>
  );
}

function NotasEntrenamiento({ workout, onAccion }: { workout: Workout; onAccion: <T>(fn: () => Promise<T>) => Promise<T | undefined> }) {
  const [notas, setNotas] = useState(workout.notas ?? '');
  return (
    <div onBlur={() => void onAccion(() => guardarNotasEntrenamiento(workout.id, notas))}>
      <TextField etiqueta="Notas del entrenamiento" valor={notas} onCambio={setNotas} maxLength={500} placeholder="Cómo te sentiste, aparatos ocupados…" />
    </div>
  );
}

/** Lo que se quitó, tal como estaba y entre quiénes, para "Deshacer". */
interface EjercicioQuitado {
  we: WorkoutExercise;
  /** Con lo que se había escrito y faltaba guardar (0.3.9). */
  series: WorkoutSet[];
  nombre: string;
  vecinos: VecinosEnSesion;
}

/**
 * El grupo (ejercicio solo o biserie) antes del cual va algo que entra en esa
 * posición de la lista en orden; `grupos.length` si va al final.
 */
function grupoDelLugar(grupos: readonly (readonly unknown[])[], posicion: number): number {
  let inicio = 0;
  for (const [i, g] of grupos.entries()) {
    if (inicio >= posicion) return i;
    inicio += g.length;
  }
  return grupos.length;
}

/**
 * Lo escrito en cada serie que todavía no se guarda (se guarda medio segundo
 * después de la última tecla), por id de serie. Quien borra la serie o quita
 * el ejercicio lo toma para que "Deshacer" lo devuelva (0.3.9).
 */
type RegistroDeLoEscrito = Map<string, () => WorkoutSet>;

function TarjetaEjercicio({
  e,
  enBiserie,
  abiertoAlInicio,
  casa,
  preferida,
  incrementoPredeterminadoKg,
  onMarcar,
  onAccion,
  onQuitar,
}: {
  e: EjercicioEnCurso;
  enBiserie: boolean;
  /** Empieza desplegado; los que ya están completos empiezan cerrados (0.11.0). */
  abiertoAlInicio: boolean;
  /** En casa: la carga es opcional y se anotan la variante y la pausa. */
  casa: boolean;
  preferida: UnidadPeso;
  /** El incremento de los ajustes, para la sugerencia de progresión (Req. 26.1). */
  incrementoPredeterminadoKg: number;
  onMarcar: (s: WorkoutSet, hecha: boolean, pendientes: CambiosSerie | null) => Promise<void>;
  onAccion: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
  onQuitar: (q: Omit<EjercicioQuitado, 'vecinos'>) => Promise<void>;
}) {
  const [quitar, setQuitar] = useState(false);
  const [abierto, setAbierto] = useState(abiertoAlInicio);
  const [masAbierto, setMasAbierto] = useState(false);
  const [notasAbiertas, setNotasAbiertas] = useState(false);
  // La carga que se impuso al usar la sugerencia: las filas que faltan la
  // muestran al momento, sin esperar a que la base avise (cada fila guarda
  // aparte lo que se escribe en ella). `n` sube con cada toque.
  const [impuesta, setImpuesta] = useState<{ valor: number; unidad: UnidadCarga; n: number } | null>(null);
  // Hora en que apareció la confirmación: un segundo toque rápido no la acepta
  const confirmacionDesde = useRef(0);
  const [borrada, setBorrada] = useState<{ serie: WorkoutSet; numero: number } | null>(null);
  // Lo escrito en sus series que falta guardar: va en lo que "Deshacer" devuelve (0.3.9)
  const loEscrito = useRef<RegistroDeLoEscrito>(new Map());
  const nombre = e.ejercicio?.nombre ?? 'el ejercicio';
  const tipo: TipoEjercicio = e.ejercicio?.tipo ?? 'peso_reps';
  // La de la próxima serie con carga; si no, la que recuerda el ejercicio o la preferida (Req. 9.5)
  const unidad: UnidadCarga = e.series.find((s) => !s.hecha)?.pesoCapturado?.unidad ?? e.ejercicio?.unidadCaptura ?? preferida;
  const ayudaEquipo = tipo === 'peso_reps' ? ayudaDeCarga(e.ejercicio?.equipo ?? 'otro', unidad) : null;
  // En casa, sin carga no es lo mismo que un dato pendiente (0.3.5): 0 = sin carga externa; vacía = sin dato
  const ayuda =
    tipo === 'peso_reps' && casa
      ? [ayudaEquipo, 'Sin carga externa: escribe 0. Vacía queda como dato pendiente.'].filter(Boolean).join(' ')
      : ayudaEquipo;
  const porLado = e.we.porLado === true;
  const conReps = tipo === 'peso_reps' || tipo === 'reps';
  const hechas = e.series.filter((s) => s.hecha).length;
  const objetivo = textoObjetivo(e.series.length, e.we.repsMin, e.we.repsMax, porLado);
  const casaAnterior = casa && e.anterior !== null ? textoPausaYVariante(e.anterior) : '';
  // Qué sugerir hoy con lo de la vez pasada (Req. 26.1). Solo lo dice: el
  // valor prellenado no cambia hasta que ella lo decida (Req. 26.2)
  const sugerencia = sugerenciaDeProgresion({
    anteriores: e.anterior?.series ?? [],
    repsMax: e.we.repsMax,
    unidad,
    ejercicio: e.ejercicio,
    incrementoPredeterminadoKg,
  });
  const porHacer = e.series.filter((s) => !s.hecha);
  const faltaSubirla =
    sugerencia.tipo === 'subir_carga' &&
    porHacer.some(
      (s) => s.pesoCapturado?.unidad !== sugerencia.unidad || Math.abs((s.pesoCapturado?.valor ?? 0) - sugerencia.a) > 1e-9,
    );
  // La carga de la próxima serie, para llevarla a la calculadora de discos
  const cargaDeHoy = porHacer.find((s) => s.pesoCapturado !== null)?.pesoCapturado ?? null;

  const laVezPasada =
    e.anterior === null
      ? 'Primera vez'
      : `La vez pasada (${fechaCorta(e.anterior.fecha)}): ${e.anterior.series.map((s) => textoSerie(s)).join(', ')}${
          casaAnterior === '' ? '' : ` · ${casaAnterior}`
        }`;
  // "Primera vez · Sin carga externa": los datos cortos del ejercicio en una línea
  const linea = [laVezPasada, casa && tipo === 'reps' ? 'Sin carga externa' : ''].filter((x) => x !== '').join(' · ');
  const notas = e.we.notas ?? '';
  // Una indicación larga se despliega en lugar de empujar las series (0.11.0)
  const notasLargas = notas.length > 110;
  const etiquetaMas = casa
    ? 'Variante, pausa e indicaciones'
    : tipo === 'peso_reps'
      ? 'Unidad de carga y más'
      : 'Más opciones';

  return (
    <article className="rounded-panel bg-superficie p-4" aria-labelledby={`ej-${e.we.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h2 id={`ej-${e.we.id}`} className="min-w-0 text-[17px] leading-snug tracking-[-0.25px] text-texto">
          {e.ejercicio?.nombre ?? 'Ejercicio'}
        </h2>
        <span className="flex shrink-0 items-center gap-1">
          <Pastilla>
            <span className="tabular-nums">
              {hechas}/{e.series.length}
            </span>
          </Pastilla>
          <button
            type="button"
            aria-expanded={abierto}
            aria-controls={`series-${e.we.id}`}
            aria-label={`${abierto ? 'Cerrar' : 'Abrir'} ${nombre}`}
            onClick={() => setAbierto((v) => !v)}
            className="grid size-9 place-items-center rounded-full text-texto-suave active:opacity-60"
          >
            <ChevronDown aria-hidden="true" className={`size-[18px] transition-transform ${abierto ? 'rotate-180' : ''}`} />
          </button>
        </span>
      </div>
      <p className="mt-0.5 text-[12px] text-texto-suave">
        {e.we.repsMin === null && e.we.repsMax === null ? '' : `${objetivo} · `}
        {enBiserie ? 'Descanso al terminar los dos' : `Descanso ${textoDescanso(e.we.descansoSeg)}`}
      </p>
      {notas !== '' && (
        <div className="mt-1">
          <p className={`text-[12px] leading-relaxed text-texto-suave ${notasLargas && !notasAbiertas ? 'line-clamp-2' : ''}`}>
            {notas}
          </p>
          {notasLargas && (
            <button
              type="button"
              aria-expanded={notasAbiertas}
              onClick={() => setNotasAbiertas((v) => !v)}
              className="flex min-h-11 items-center text-[12px] font-medium text-marca active:opacity-60"
            >
              {notasAbiertas ? 'Ver menos' : 'Ver indicaciones completas'}
            </button>
          )}
        </div>
      )}
      <p className="mt-1 text-[12px] text-texto-suave">{linea}</p>
      {porLado && (
        <p className="mt-1 text-[12px] text-texto-suave">Anota las de cada lado: los dos lados cuentan como una serie.</p>
      )}
      {sugerencia.tipo !== 'ninguna' && (
        <div className="mt-2 rounded-[13px] bg-marca-suave px-3 py-2.5">
          <p className="text-[12px] leading-relaxed text-texto">{sugerencia.texto}</p>
          {sugerencia.tipo === 'subir_carga' && faltaSubirla && (
            <button
              type="button"
              className="mt-1.5 flex min-h-11 items-center text-[13px] font-medium text-marca active:opacity-60"
              onClick={() =>
                void onAccion(async () => {
                  const cambiadas = await fijarCargaDeLasQueFaltan(e.we.id, {
                    valor: sugerencia.a,
                    unidad: sugerencia.unidad,
                  });
                  setImpuesta((antes) => ({
                    valor: sugerencia.a,
                    unidad: sugerencia.unidad,
                    n: (antes?.n ?? 0) + 1,
                  }));
                  return cambiadas;
                })
              }
            >
              Usar {formatearNumero(sugerencia.a, { decimales: 2, fijos: false })} {sugerencia.unidad} en las que faltan
            </button>
          )}
        </div>
      )}
      {abierto && (
        <div id={`series-${e.we.id}`} className="mt-3 border-t border-borde pt-2">
          {/* Los encabezados de la tabla; con repeticiones por lado la fila
              lleva dos columnas anidadas y no se alinearían */}
          {!(conReps && porLado) && (
            <div aria-hidden="true" className="flex items-center gap-2 px-2 pb-1 text-[11px] text-texto-suave">
              <span className="w-11 shrink-0">Serie</span>
              {tipo === 'peso_reps' && <span className="min-w-0 flex-1">Carga</span>}
              {conReps && <span className="min-w-0 flex-1">Reps</span>}
              {tipo === 'distancia' && <span className="min-w-0 flex-1">Distancia</span>}
              {(tipo === 'tiempo' || tipo === 'distancia') && <span className="min-w-0 flex-1">Tiempo</span>}
              {onMarcar !== undefined && <span className="w-14 shrink-0 text-center">Hecha</span>}
              <span className="w-9 shrink-0" />
            </div>
          )}

          <ol className="space-y-1">
            {e.series.map((s, i) => {
              // Cada serie con la unidad en que se capturó: una hecha en lb sigue en lb
              // aunque el ejercicio ya vaya en kg (si no, al salir del campo se guardaría como kg)
              const deLaSerie = s.pesoCapturado?.unidad ?? unidad;
              return (
                <FilaSerie
                  key={`${s.id}-${deLaSerie}`}
                  serie={s}
                  numero={i + 1}
                  tipo={tipo}
                  porLado={porLado}
                  unidad={deLaSerie}
                  impuesta={impuesta}
                  nombreEjercicio={nombre}
                  onMarcar={onMarcar}
                  onAccion={onAccion}
                  onBorrada={(serie, numero) => setBorrada({ serie, numero })}
                  loEscrito={loEscrito.current}
                />
              );
            })}
          </ol>

          {borrada !== null && (
            <AvisoDeshacer
              texto={`Borraste la serie ${borrada.numero}${tieneDatos(borrada.serie) ? ` (${textoSerie(borrada.serie)})` : ''}. Las demás no cambiaron.`}
              onDeshacer={() =>
                void onAccion(async () => {
                  await restaurarSerie(borrada.serie);
                  setBorrada(null);
                })
              }
              onCerrar={() => setBorrada(null)}
            />
          )}

          {/* Agregar una serie va con las series; quitar el ejercicio completo va aparte (incidencia del 26 oct 2026) */}
          <div className="mt-2">
            <Button variante="secundario" icono={Plus} onClick={() => void onAccion(() => agregarSerie(e.we.id))}>
              Agregar serie
            </Button>
          </div>

          <div className="mt-2 border-t border-borde pt-1">
            <Chip
              icono={SlidersHorizontal}
              texto={etiquetaMas}
              abierto={masAbierto}
              etiqueta={`Más opciones de ${nombre}`}
              onClick={() => setMasAbierto((v) => !v)}
            />
          </div>

          {masAbierto && (
            <div className="mt-2 space-y-3">
              {casa && <PausaYVarianteDeEjercicio e={e} onAccion={onAccion} />}

              {tipo === 'peso_reps' && (
                <div>
                  <Segmentado
                    etiqueta={`Unidad de carga de ${e.ejercicio?.nombre ?? 'este ejercicio'}`}
                    completo
                    valor={unidad}
                    onCambio={(u) => void onAccion(() => cambiarUnidadEnEntrenamiento(e.we.id, u))}
                    opciones={UNIDADES_CARGA.map((u) => ({ valor: u, etiqueta: ETIQUETAS_UNIDAD_CARGA[u] }))}
                  />
                  {ayuda !== null && <p className="mt-1.5 text-[12px] text-texto-suave">{ayuda}</p>}
                  {/* Con barra, la calculadora de discos con la carga de hoy (Req. 26.3) */}
                  {e.ejercicio?.equipo === 'barra' && unidad !== 'placa' && (
                    <Link
                      to={`/gym/discos${cargaDeHoy === null ? '' : `?objetivo=${cargaDeHoy.valor}&unidad=${cargaDeHoy.unidad}`}`}
                      className="mt-1.5 flex min-h-11 items-center text-[13px] font-medium text-marca active:opacity-60"
                    >
                      ¿Qué discos pongo?
                    </Link>
                  )}
                </div>
              )}

              {quitar ? (
                <div role="group" aria-label={`Quitar ejercicio: ${nombre}`} className="space-y-3 rounded-[15px] bg-alerta-suave p-3">
                  <p className="text-[13px] leading-relaxed text-texto">
                    ¿Quitar {nombre} de este entrenamiento? {alcanceDeQuitar(e.series)} Lo puedes deshacer justo después.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variante="secundario" onClick={() => setQuitar(false)}>
                      Cancelar
                    </Button>
                    <Button
                      variante="peligro"
                      onClick={() => {
                        // Un doble toque sobre "Quitar ejercicio" no confirma: la confirmación espera un momento
                        if (Date.now() - confirmacionDesde.current < 600) return;
                        // Con lo escrito en el último medio segundo, que todavía no se guardaba (0.3.9)
                        const series = e.series.map((s) => loEscrito.current.get(s.id)?.() ?? s);
                        void onQuitar({ we: e.we, series, nombre });
                      }}
                    >
                      Sí, quitar ejercicio
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variante="fantasma"
                  icono={X}
                  aria-label={`Quitar ejercicio: ${nombre}`}
                  onClick={() => {
                    confirmacionDesde.current = Date.now();
                    setQuitar(true);
                  }}
                >
                  Quitar ejercicio
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

const TEXTO_PANTALLA: Record<Exclude<EstadoPantalla['tipo'], 'sin_soporte' | 'apagada' | 'rechazada'>, string> = {
  pidiendo: 'Activando…',
  activa: 'Activa: la pantalla no se apaga sola mientras DuoFit esté a la vista.',
  falta_toque: 'Todavía no está activa: el iPhone la concede con un toque. Toca cualquier parte de la pantalla.',
  liberada: 'El sistema la soltó. Toca la pantalla para volver a activarla.',
};

/**
 * El estado real de "Mantener la pantalla encendida" (0.3.8), no solo la
 * casilla: activa, falta un toque, soltada por el sistema o negada con su
 * motivo. Debajo, lo anotado en este teléfono para revisarlo.
 */
function EstadoDePantalla({ estado }: { estado: EstadoPantalla }) {
  const registro = controlPantalla().registro;
  const texto =
    estado.tipo === 'rechazada'
      ? `No se pudo activar (${estado.motivo}). La pantalla se apagará como siempre.`
      : estado.tipo === 'sin_soporte' || estado.tipo === 'apagada'
        ? null
        : TEXTO_PANTALLA[estado.tipo];
  return (
    <div className="space-y-1 pl-1">
      <p aria-live="polite" className={`text-[12px] leading-relaxed ${estado.tipo === 'activa' ? 'text-exito' : 'text-texto-suave'}`}>
        {texto}
      </p>
      {registro.length > 0 && (
        <details>
          <summary className="flex min-h-11 cursor-pointer items-center text-[12px] font-medium text-marca">
            Lo que pasó con la pantalla encendida
          </summary>
          <ul className="space-y-1 pb-1 text-[12px] leading-relaxed text-texto-suave">
            {[...registro].reverse().map((e) => (
              <li key={`${e.en}-${e.texto}`}>
                {horaLocalDe(e.en)} · {e.texto}
                {/* Lo mismo varias veces seguidas va en una línea (0.3.9) */}
                {(e.veces ?? 1) > 1 && ` (${e.veces} veces seguidas desde las ${horaLocalDe(e.desde ?? e.en)})`}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** ¿La serie tiene algo anotado? */
function tieneDatos(s: WorkoutSet): boolean {
  return (
    s.hecha ||
    s.pesoCapturado !== null ||
    s.reps !== null ||
    (s.repsIzq ?? null) !== null ||
    (s.repsDer ?? null) !== null ||
    s.duracionSeg !== null ||
    s.distanciaM !== null
  );
}

/** "Se borran sus 4 series (1 hecha) con lo que anotaste." */
function alcanceDeQuitar(series: readonly WorkoutSet[]): string {
  if (series.length === 0) return 'No tiene series.';
  const hechas = series.filter((s) => s.hecha).length;
  const conDatos = series.some(tieneDatos);
  return `Se ${series.length === 1 ? 'borra su serie' : `borran sus ${series.length} series`}${
    hechas === 0 ? '' : ` (${hechas === 1 ? '1 hecha' : `${hechas} hechas`})`
  }${conDatos ? ' con lo que anotaste' : ''}.`;
}

/** Aviso con "Deshacer" después de borrar algo, en el lugar donde estaba. */
export function AvisoDeshacer({ texto, onDeshacer, onCerrar }: { texto: string; onDeshacer: () => void; onCerrar: () => void }) {
  return (
    <div role="status" className="mt-2 space-y-2 rounded-[15px] bg-info-suave p-3">
      <p className="text-[13px] leading-relaxed text-texto">{texto}</p>
      <div className="grid grid-cols-2 gap-2">
        <Button variante="secundario" onClick={onDeshacer}>
          Deshacer
        </Button>
        <Button variante="fantasma" onClick={onCerrar}>
          Cerrar aviso
        </Button>
      </div>
    </div>
  );
}

const textoNumero = (v: number | null) => (v === null ? '' : String(v));

/**
 * Una serie con sus campos, que se guardan al momento. En el entrenamiento en
 * curso lleva la palomita para marcarla; en el historial (0.3.7) ya está hecha
 * y solo se corrige (`onMarcar` ausente).
 */
export function FilaSerie({
  serie,
  numero,
  tipo,
  porLado,
  unidad,
  nombreEjercicio,
  onMarcar,
  onAccion,
  avisoAlQuitar,
  onBorrada,
  loEscrito,
  impuesta = null,
}: {
  serie: WorkoutSet;
  numero: number;
  tipo: TipoEjercicio;
  /** Las repeticiones de cada lado en la misma serie (0.3.5). */
  porLado: boolean;
  unidad: UnidadCarga;
  nombreEjercicio: string;
  /**
   * Una carga que la pantalla acaba de poner en las series que faltan, al
   * usar la sugerencia de progresión (Req. 26.2; 0.8.1). Con cada toque sube
   * su `n`, para que el campo la muestre aunque sea la misma carga.
   */
  impuesta?: { valor: number; unidad: UnidadCarga; n: number } | null;
  onMarcar?: (s: WorkoutSet, hecha: boolean, pendientes: CambiosSerie | null) => Promise<void>;
  onAccion: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
  /** Si quitar esta serie quita algo más, se pide confirmar con este texto. */
  avisoAlQuitar?: string;
  /** Después de borrar solo esta serie, para ofrecer "Deshacer" (0.3.8). */
  onBorrada?: (serie: WorkoutSet, numero: number) => void;
  /** Donde la fila deja cómo tomar lo escrito que falta guardar, para "Quitar ejercicio" (0.3.9). */
  loEscrito?: RegistroDeLoEscrito;
}) {
  const [peso, setPeso] = useState(textoNumero(serie.pesoCapturado?.valor ?? null));
  const [reps, setReps] = useState(textoNumero(serie.reps));
  const [izq, setIzq] = useState(textoNumero(serie.repsIzq ?? null));
  const [der, setDer] = useState(textoNumero(serie.repsDer ?? null));
  const [segundos, setSegundos] = useState(textoNumero(serie.duracionSeg));
  const [km, setKm] = useState(serie.distanciaM === null ? '' : String(serie.distanciaM / 1000));
  const [detalles, setDetalles] = useState(false);
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);
  const espera = useRef<number | null>(null);
  const conReps = tipo === 'peso_reps' || tipo === 'reps';

  // La carga que se impuso al usar la sugerencia: solo en las que faltan
  const impuestoN = impuesta?.n ?? 0;
  const impuestoValor = impuesta?.valor ?? null;
  useEffect(() => {
    if (impuestoN === 0 || impuestoValor === null || serie.hecha) return;
    setPeso(textoNumero(impuestoValor));
  }, [impuestoN, impuestoValor, serie.hecha]);

  function cambios(): CambiosSerie | string {
    const c: CambiosSerie = {};
    if (tipo === 'peso_reps') {
      const v = peso.trim() === '' ? null : leerDecimal(peso);
      if (peso.trim() !== '' && v === null) return 'Revisa la carga.';
      c.pesoCapturado = v === null ? null : { valor: v, unidad };
    }
    const entero = (t: string): number | null | string => {
      const r = t.trim() === '' ? null : leerDecimal(t);
      return t.trim() !== '' && (r === null || !Number.isInteger(r)) ? 'Las repeticiones deben ser un número entero.' : r;
    };
    if (conReps && porLado) {
      const i = entero(izq);
      const d = entero(der);
      if (typeof i === 'string') return i;
      if (typeof d === 'string') return d;
      c.repsIzq = i;
      c.repsDer = d;
    } else if (conReps) {
      const r = entero(reps);
      if (typeof r === 'string') return r;
      c.reps = r;
    }
    if (tipo === 'tiempo' || tipo === 'distancia') {
      const t = segundos.trim() === '' ? null : leerDecimal(segundos);
      if (segundos.trim() !== '' && t === null) return 'Revisa los segundos.';
      c.duracionSeg = t;
    }
    if (tipo === 'distancia') {
      const d = km.trim() === '' ? null : leerDecimal(km);
      if (km.trim() !== '' && d === null) return 'Revisa la distancia.';
      c.distanciaM = d === null ? null : d * 1000;
    }
    return c;
  }

  function guardarYa() {
    if (espera.current !== null) window.clearTimeout(espera.current);
    espera.current = null;
    const c = cambios();
    if (typeof c === 'string') return;
    void onAccion(() => actualizarSerie(serie.id, c));
  }
  // La espera llama al guardar del último dibujo: con el de cuando se escribió
  // la tecla, guardaría lo que había antes de esa tecla (0.3.7)
  const guardarUltimo = useRef(guardarYa);
  guardarUltimo.current = guardarYa;

  function programar() {
    if (espera.current !== null) window.clearTimeout(espera.current);
    espera.current = window.setTimeout(() => guardarUltimo.current(), 500);
  }

  // Al desaparecer la fila, lo que faltaba guardar se guarda en lugar de perderse
  useEffect(
    () => () => {
      if (espera.current !== null) guardarUltimo.current();
    },
    [],
  );

  /**
   * La serie como está en pantalla, con lo escrito que faltaba guardar; deja
   * de esperar para guardarlo porque la serie se va a borrar y lo escrito va
   * en lo que "Deshacer" devuelve.
   */
  function tomarLoEscrito(): WorkoutSet {
    if (espera.current !== null) window.clearTimeout(espera.current);
    espera.current = null;
    const c = cambios();
    if (typeof c === 'string') return serie;
    return {
      ...serie,
      ...c,
      ...('pesoCapturado' in c
        ? { pesoKg: c.pesoCapturado == null ? null : capturarCarga(c.pesoCapturado.valor, c.pesoCapturado.unidad).pesoKg }
        : {}),
    };
  }
  const tomarUltimo = useRef(tomarLoEscrito);
  tomarUltimo.current = tomarLoEscrito;
  useEffect(() => {
    if (loEscrito === undefined) return;
    const tomar = () => tomarUltimo.current();
    loEscrito.set(serie.id, tomar);
    return () => {
      if (loEscrito.get(serie.id) === tomar) loEscrito.delete(serie.id);
    };
  }, [loEscrito, serie.id]);

  const campo = (etiqueta: string, valor: string, setValor: (v: string) => void, sufijo: string, decimal = true) => (
    <label className="grid min-w-0 flex-1 gap-0.5">
      <span className="flex items-center gap-1 rounded-[13px] border border-borde-fuerte bg-fondo px-2.5">
        <input
          aria-label={`${etiqueta} de la serie ${numero} de ${nombreEjercicio}`}
          inputMode={decimal ? 'decimal' : 'numeric'}
          value={valor}
          onChange={(ev) => {
            setValor(ev.target.value);
            programar();
          }}
          onBlur={guardarYa}
          className="min-h-12 w-full min-w-0 bg-transparent text-[17px] tabular-nums text-texto outline-none"
        />
        <span className="shrink-0 text-[12px] text-texto-suave">{sufijo}</span>
      </span>
    </label>
  );

  const sufijoCarga = unidad === 'placa' ? 'placa' : unidad;

  return (
    <li className={`rounded-[15px] p-2 ${serie.hecha && onMarcar !== undefined ? 'bg-marca-suave' : ''}`}>
      <div className="flex items-center gap-2">
        <span
          aria-label={`Serie ${numero}${serie.tipoSerie === 'normal' ? '' : `, ${TIPOS_SERIE.find((t) => t.valor === serie.tipoSerie)?.etiqueta}`}`}
          className="grid size-11 shrink-0 place-items-center rounded-full bg-superficie-suave text-[13px] font-medium tabular-nums text-texto"
        >
          {serie.tipoSerie === 'calentamiento' ? 'C' : numero}
        </span>
        {conReps && porLado ? (
          // Por lado: los dos lados en la misma fila; con carga, la carga arriba para que quepa en 320 px
          <div className="grid min-w-0 flex-1 gap-2">
            {tipo === 'peso_reps' && <div className="flex">{campo('Carga', peso, setPeso, sufijoCarga)}</div>}
            <div className="flex gap-2">
              {campo('Repeticiones izquierda', izq, setIzq, 'izq.', false)}
              {campo('Repeticiones derecha', der, setDer, 'der.', false)}
            </div>
          </div>
        ) : (
          <>
            {tipo === 'peso_reps' && campo('Carga', peso, setPeso, sufijoCarga)}
            {conReps && campo('Repeticiones', reps, setReps, 'reps', false)}
          </>
        )}
        {tipo === 'distancia' && campo('Distancia', km, setKm, 'km')}
        {(tipo === 'tiempo' || tipo === 'distancia') && campo('Tiempo', segundos, setSegundos, 's', false)}
        {onMarcar !== undefined && (
          <button
            type="button"
            aria-pressed={serie.hecha}
            aria-label={serie.hecha ? `Desmarcar la serie ${numero} de ${nombreEjercicio}` : `Marcar hecha la serie ${numero} de ${nombreEjercicio}`}
            onClick={() => {
              const c = cambios();
              if (typeof c === 'string') return void onAccion(async () => Promise.reject(new Error(c)));
              if (espera.current !== null) window.clearTimeout(espera.current);
              espera.current = null;
              void onMarcar(serie, !serie.hecha, c);
            }}
            className={`grid size-14 shrink-0 place-items-center rounded-[17px] ${serie.hecha ? 'bg-marca text-sobre-marca' : 'border-2 border-borde-fuerte bg-superficie text-texto-suave'}`}
          >
            <Check className="size-6" aria-hidden="true" />
          </button>
        )}
        {/* Un menú visible por serie: tipo de serie, RIR y borrarla (0.11.0) */}
        <button
          type="button"
          aria-expanded={detalles}
          aria-controls={`detalles-${serie.id}`}
          aria-label={`Más de la serie ${numero} de ${nombreEjercicio}`}
          onClick={() => setDetalles((d) => !d)}
          className="grid size-9 shrink-0 place-items-center rounded-full text-texto-suave active:bg-superficie-suave"
        >
          <MoreVertical className="size-[18px]" aria-hidden="true" />
        </button>
      </div>
      {detalles && (
        <div id={`detalles-${serie.id}`} className="mt-2 grid grid-cols-2 gap-2">
          <label className="grid gap-1 text-[12px] text-texto-suave">
            Tipo de serie
            <select
              value={serie.tipoSerie}
              onChange={(ev) => void onAccion(() => actualizarSerie(serie.id, { tipoSerie: ev.target.value as TipoSerie }))}
              className="min-h-11 rounded-[13px] border border-borde-fuerte bg-superficie px-2 text-[14px] text-texto"
            >
              {TIPOS_SERIE.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.etiqueta}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-[12px] text-texto-suave">
            RIR (opcional)
            <select
              value={serie.rir === null ? '' : String(serie.rir)}
              onChange={(ev) => void onAccion(() => actualizarSerie(serie.id, { rir: ev.target.value === '' ? null : Number(ev.target.value) }))}
              className="min-h-11 rounded-[13px] border border-borde-fuerte bg-superficie px-2 text-[14px] text-texto"
            >
              <option value="">Sin dato</option>
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {confirmarQuitar && avisoAlQuitar !== undefined ? (
            <div className="col-span-2 space-y-2 rounded-[13px] bg-alerta-suave p-3">
              <p className="text-[13px] text-texto">{avisoAlQuitar}</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variante="peligro" onClick={() => void onAccion(() => quitarSerie(serie.id))}>
                  Sí, borrar serie
                </Button>
                <Button variante="secundario" onClick={() => setConfirmarQuitar(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            // Solo esta serie: el ejercicio y las demás series no se tocan (0.3.8)
            <Button
              variante="fantasma"
              icono={Trash2}
              aria-label={`Borrar serie ${numero} de ${nombreEjercicio}`}
              onClick={() => {
                if (avisoAlQuitar !== undefined) return setConfirmarQuitar(true);
                // Lo escrito que faltaba guardar va en lo que "Deshacer" devuelve
                const comoEstaba = tomarLoEscrito();
                void onAccion(async () => {
                  await quitarSerie(serie.id);
                  onBorrada?.(comoEstaba, numero);
                });
              }}
            >
              Borrar serie {numero}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Lo que dice la barra del descanso del aviso con la pantalla bloqueada
 * (0.12.0). Con la opción apagada no dice nada: el aviso con la app a la
 * vista es el de siempre y no hace falta explicarlo cada descanso.
 */
const TEXTO_AVISO: Record<EstadoAviso['tipo'], string | null> = {
  a_la_vista: null,
  listo: 'Suena aunque bloquees el iPhone',
  falta_toque: 'Toca la pantalla para que suene bloqueada',
  pausado: 'Otro audio lo pausó: solo suena con la app a la vista',
  sin_soporte: 'Este navegador solo avisa con la app a la vista',
};

/** El estado real del aviso, para no prometer lo que no está pasando. */
function useEstadoDelAviso(): EstadoAviso {
  const [estado, setEstado] = useState<EstadoAviso>(() => estadoDelAviso());
  useEffect(() => escucharEstadoDelAviso(setEstado), []);
  return estado;
}

/** Barra fija del descanso (Req. 12): ±15 s, saltar, fin visible y anunciado, sin parpadeo. */
function BarraDescanso({
  workout,
  aviso,
  onAccion,
}: {
  workout: Workout;
  aviso: string | null;
  onAccion: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
}) {
  const ahora = useAhora(250);
  const descanso = workout.descanso;
  const [anunciado, setAnunciado] = useState<string | null>(null);
  const avisoAudio = useEstadoDelAviso();
  const restante = descanso === null ? null : segundosRestantes(descanso, ahora);
  const terminado = restante !== null && restante <= 0;

  useEffect(() => {
    if (descanso !== null && terminado && anunciado !== descanso.terminaEn) {
      setAnunciado(descanso.terminaEn);
      // El banner del sistema, que con el audio corriendo también sale con la
      // pantalla bloqueada; sin permiso no hace nada (0.12.0)
      void avisarFinDeDescanso(descanso.terminaEn, 'Toca para volver a tu entrenamiento.');
    }
  }, [descanso, terminado, anunciado]);

  if (descanso === null || restante === null) {
    return aviso === null ? null : (
      <div className="fixed inset-x-0 bottom-[calc(66px+max(15px,env(safe-area-inset-bottom)))] z-20 px-3">
        <p role="status" className="mx-auto max-w-lg rounded-panel bg-info-suave px-4 py-3 text-[13px] text-texto shadow-[0_1px_3px_var(--sombra)]">
          {aviso}
        </p>
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-[calc(66px+max(15px,env(safe-area-inset-bottom)))] z-20 px-3">
      <div
        className={`mx-auto max-w-lg rounded-panel px-4 py-3 shadow-[0_1px_3px_var(--sombra)] ${terminado ? 'bg-marca text-sobre-marca' : 'bg-superficie text-texto'}`}
      >
        <p className="sr-only" aria-live="assertive">
          {anunciado === descanso.terminaEn ? 'Terminó el descanso.' : ''}
        </p>
        {/* Si el aviso con la pantalla bloqueada está encendido, su estado real */}
        {TEXTO_AVISO[avisoAudio.tipo] !== null && !terminado && (
          <p className={`mb-1 text-[11px] ${avisoAudio.tipo === 'listo' ? 'text-exito' : 'text-aviso'}`}>
            {TEXTO_AVISO[avisoAudio.tipo]}
          </p>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className={`text-[11px] uppercase tracking-[1.5px] ${terminado ? '' : 'text-texto-suave'}`}>
              {terminado ? 'Descanso terminado' : 'Descanso'}
            </p>
            <p className="text-[30px] leading-none tabular-nums tracking-[-1px]">
              {terminado ? `hace ${formatearReloj(-restante)}` : formatearReloj(restante)}
            </p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            {!terminado && (
              <>
                {/* El toque también reanuda el audio si iOS lo dejó en pausa al volver */}
                <Button
                  variante="secundario"
                  completo={false}
                  onClick={() => {
                    prepararSonido();
                    void onAccion(() => cambiarDescanso(workout.id, -15));
                  }}
                >
                  −15 s
                </Button>
                <Button
                  variante="secundario"
                  completo={false}
                  onClick={() => {
                    prepararSonido();
                    void onAccion(() => cambiarDescanso(workout.id, 15));
                  }}
                >
                  +15 s
                </Button>
              </>
            )}
            <Button
              variante={terminado ? 'secundario' : 'fantasma'}
              completo={false}
              icono={terminado ? X : SkipForward}
              onClick={() => void onAccion(() => cambiarDescanso(workout.id, 'saltar'))}
            >
              {terminado ? 'Cerrar' : 'Saltar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
